"use client";

import { motion } from "framer-motion";
import { deliverySteps, maturityLevels } from "@/lib/data/site";
import { SectionHeader } from "@/components/ui/section-header";
import { useState } from "react";
import { cn } from "@/lib/utils";

export function DeliveryModel() {
  return (
    <section className="relative overflow-x-clip py-16 sm:py-24 md:py-32">
      <div className="container-wide min-w-0">
        <SectionHeader
          eyebrow="Delivery Model"
          title={"FROM IDEA\nTO INTELLIGENCE"}
          description="A clear path from discovery to continuous optimization — designed for enterprise velocity."
        />
        <div className="h-scroll mt-10 flex gap-3 pb-4 snap-x sm:mt-14 sm:gap-4">
          {deliverySteps.map((step, index) => (
            <motion.div
              key={step.step}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="min-w-[min(78vw,260px)] snap-start rounded-[1.5rem] border border-border bg-card p-5 sm:min-w-[220px]"
            >
              <p className="font-mono text-xs text-accent">{step.step}</p>
              <h3 className="mt-3 font-display text-2xl tracking-tight">{step.title}</h3>
              <p className="mt-3 text-sm text-muted">{step.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function AiMaturityModel() {
  const [active, setActive] = useState(2);
  const level = maturityLevels[active];

  return (
    <section className="relative py-16 sm:py-24 md:py-32">
      <div className="container-wide">
        <SectionHeader
          eyebrow="AI Maturity Model"
          title={"WHERE ARE YOU\nON THE JOURNEY?"}
          description="A practical framework to assess readiness — from digital foundation to agentic enterprise."
        />

        <div className="mt-14 grid gap-6 lg:grid-cols-[1fr_1.1fr]">
          <div className="space-y-2">
            {maturityLevels.map((item, index) => (
              <button
                key={item.level}
                type="button"
                onClick={() => setActive(index)}
                onMouseEnter={() => setActive(index)}
                className={cn(
                  "w-full rounded-2xl border px-5 py-4 text-left transition",
                  active === index
                    ? "border-accent/50 bg-card shadow-[var(--shadow-glow)]"
                    : "border-border hover:border-border-strong",
                )}
              >
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-display text-xl tracking-tight">{item.title}</h3>
                  <span className="font-mono text-xs text-accent">LEVEL {item.level}</span>
                </div>
              </button>
            ))}
          </div>

          <motion.div
            key={level.level}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-[2rem] border border-border bg-gradient-to-br from-accent/10 via-card to-cyan/10 p-8"
          >
            <p className="eyebrow mb-4">Level {level.level}</p>
            <h3 className="font-display text-4xl tracking-tight">{level.title}</h3>
            <div className="mt-8 grid gap-5 sm:grid-cols-2">
              <div>
                <p className="text-xs uppercase tracking-[0.16em] text-muted">Characteristics</p>
                <p className="mt-2 text-sm leading-relaxed">{level.characteristics}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.16em] text-muted">Technology needs</p>
                <p className="mt-2 text-sm leading-relaxed">{level.requirements}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.16em] text-muted">Typical challenges</p>
                <p className="mt-2 text-sm leading-relaxed">{level.challenges}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.16em] text-muted">Recommended next step</p>
                <p className="mt-2 text-sm leading-relaxed">{level.next}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
